import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { MaintenanceMode } from 'src/app/shared/models/maintenance-mode.model';

/**
 * Settings service for communicating with the API backend.
 *
 * @export
 * @class SettingsService
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  /**
   * Creates an instance of SettingsService.
   *
   * @param {HttpClient} http Angular HTTP service.
   * @param {string} baseApiUrl Base API URL.
   * @memberof SettingsService
   */
  constructor(
    private http: HttpClient,
    @Inject('BASE_API_URL') private baseApiUrl: string
  ) { }

  /**
   * Check whether Maintenance Mode is currently enabled, or not.
   *
   * @return {Observable<MaintenanceMode>} Maintenance Mode object.
   * @memberof SettingsService
   */
  public checkMaintenanceMode() {
    return this.http.get<MaintenanceMode>(`${this.baseApiUrl}/status`);
  }

  /**
   * Turn Maintenance Mode on or off.
   *
   * @param {boolean} isEnabled Whether Maintenance Mode should be enabled.
   * @param {string} msg Message to show users who view the site while Maintenance Mode is enabled.
   * @return {Observable<boolean>} True/false was successful.
   * @memberof SettingsService
   */
  public toggleMaintenanceMode(isEnabled: boolean, msg: string) {
    return this.http.post<boolean>(`${this.baseApiUrl}/admin/settings/maintenance`, {
      isEnabled,
      message: msg
    });
  }

  /**
   * Resync the Reports and Clients in the database with Power BI.
   *
   * @return {Observable<boolean>} True/false was successful.
   * @memberof SettingsService
   */
  public resync() {
    return this.http.get<boolean>(`${this.baseApiUrl}/admin/settings/resync`);
  }

  /**
   * Reset all User report access tokens.
   *
   * @return {Observable<boolean>} True/false was successful.
   * @memberof SettingsService
   */
  public tokenReset() {
    return this.http.get<boolean>(`${this.baseApiUrl}/admin/settings/token-reset`);
  }

  /**
   * Get the default Power BI Dataset ID currently in use.
   *
   * @return {Observable<boolean>} True/false was successful.
   * @memberof SettingsService
   */
  public defaultDatasetId() {
    return this.http.get(`${this.baseApiUrl}/admin/settings/default-dataset`, { responseType: 'text' });
  }
}
